<template>
  <div>
    <hr>
    <ring-loader v-if="loading"/>
    <div v-else class="portlet-body form">
      <div class="row">
        <div v-if="canEdit" class="col-sm-3">
          <singleselect
            v-model="chosenType"
            :checked="chosenType"
            :options="availableTypes"
            :title="trans('menu_planner.entity.brand')"
            class="planner-multiselect"
            label="value"
            name="typesSelect"
          />
        </div>
        <div v-if="canEdit" class="col-sm-8">
          <button class="btn btn-success" @click="addType">
            <i class="fa fa-plus"></i>
            {{ trans('menu_planner_select.add_type') }}
          </button>
        </div>
        <div class="btn-set pull-right">
          <button class="btn btn-success margin-right-10" @click="showModal">
            {{ trans('menu_planner_select.copy_from_day') }}
          </button>
          <button class="btn btn-info" @click="back">
            {{ trans('menu_planner_select.back_to_show') }}
          </button>
        </div>
      </div>
      <hr>
      <template v-for="(item, position) in groupedExpressMeals">
        <menu-planner-select-type
          :key="position"
          :brand="brand"
          :date="date"
          :express-variant-id="expressVariantId"
          :planned-meals="item.meals"
          :available-meals="meals"
          :name="item.selectTypeName"
          :type-id="item.selectTypeId"
          :can-edit="canEdit"
          @remove="removeType"
        />
      </template>
    </div>
    <div id="modal" class="modal fade" tabindex="-1" role="dialog" aria-labelledby="modalLabel" aria-hidden="true">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h3 id="modalLabel">{{ trans('menu_planner_select.copying_title') }}
              <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </h3>
          </div>
          <div class="modal-body">
            <div class="form-group">
              <label class="col-form-label">
                {{ trans('menu_planner_select.choose_brand') }}:
              </label>
              <singleselect
                v-model="fromBrand"
                :allow-empty="false"
                :checked="brand"
                :options="copyFromBrands"
                :title="trans('menu_planner.entity.brand')"
                class="planner-multiselect"
                name="brand"
              />
            </div>
            <div class="form-group">
              <label class="col-form-label">
                {{ trans('menu_planner_select.choose_date') }}:
              </label>
              <date-picker
                v-model="copyFromDate"
                :time="false"
                :init-value="false"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-success" :disabled="!copyFromDate" @click="copyMenu">
              {{ trans('menu_planner_select.copy') }}
            </button>
            <button type="button" class="btn default" data-dismiss="modal">
              {{ trans('form.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  NTFY_DE_SYMBOL,
  EXPRESS_DE_SYMBOL,
  EXPRESS_SYMBOL,
  WORK_TAG_EXPRESS_ID,
  COPY_SELECT_FROM_BRAND_MAP
} from '@const/common'
import RingLoader from 'vue-spinner/src/RingLoader.vue'
import singleselect from '@component/singleselect.vue'
import MenuPlannerSelectType from '@component/view/MenuPlannerSelectType.vue'
import moment from 'moment/moment'
import datePicker from '@component/datePicker.vue'

export default {
  components: {
    MenuPlannerSelectType,
    singleselect,
    RingLoader,
    datePicker,
  },
  props: {
    date: {
      type: [String, Date],
      default: '',
    },
    brand: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      meals: [],
      expressMeals: [],
      groupedExpressMeals: null,
      expressVariantId: null,
      brandInfo: [],
      loading: 0,
      typesSelect: [],
      availableTypes: [],
      chosenType: null,
      availableBrands: [],
      copyFromDate: null,
      fromBrand: null,
    }
  },
  computed: {
    canEdit() {
      return this.isGranted &&
        (new moment(this.date)).unix() >= (new moment((new moment()).format('YYYY-MM-DD'))).unix()
    },
    copyFromBrands() {
      return this.availableBrands.filter(item => COPY_SELECT_FROM_BRAND_MAP[this.brandInfo.symbol].includes(item.symbol))
    },
    isGranted() {
      return Object.values(window.appValues.menuPlannerSelect.permissions).includes('MENU_PLANNER_SELECT_UPDATE')
    },
  },
  async mounted() {
    await this.getBrandInfo()
    await this.getExpressVariant()
    await this.getTypesSelect()
    this.getMeals()
    this.getPlannedMeals()
    this.availableBrands = this.$store.state.availableBrands
  },
  methods: {
    async getBrandInfo() {
      this.loading++
      let url = Routing.generate('api_get_brand', {
        id: this.brand,
        context: 'dropdown',
      })
      let response = await this.$http.get(url)
      this.brandInfo = response.data
    },
    async getExpressVariant() {
      const url = Routing.generate('api_get_variant_by_code', {
        code: this.brandInfo.symbol === NTFY_DE_SYMBOL ? EXPRESS_DE_SYMBOL : EXPRESS_SYMBOL,
        context: 'dropdown'
      })
      let response = await this.$http.get(url)
      this.expressVariantId = response.data.id
      this.loading--
    },
    getMeals() {
      this.loading++
      let url = Routing.generate('api_get_meals_final_by_variant_excluded', {
        variant: this.expressVariantId,
        context: 'dropdown',
        workTag: WORK_TAG_EXPRESS_ID,
        date: this.date,
      })
      this.$http.get(url).then((response) => {
        let meals = []
        for (let type in response.data) {
          response.data[type].forEach(meal => {
            meal.type = type
            meals.push(meal)
          })
        }
        meals.sort((a, b) => a.name.localeCompare(b.name))
        this.meals = meals
        this.loading--
      }, response => {
        this.handleAjaxError(response)
      })
    },
    async getTypesSelect() {
      this.loading++
      let url = Routing.generate('api_get_tags', {
        type: 'types_select',
        context: 'dropdown',
      })
      let response = await this.$http.get(url)
      this.typesSelect = this.availableTypes = response.data
      this.loading--
    },
    getPlannedMeals() {
      this.loading++
      let url = Routing.generate('api_get_meal_menu_planner', {
        date: this.date,
        brand: this.brand,
        context: 'menu_planner',
      })
      this.$http.get(url).then(response => {
        this.expressMeals = response.data
        this.$forceUpdate()
        this.groupExpressMeals()
        this.loading--
      }, response => {
        this.handleAjaxError(response)
      })
    },
    groupExpressMeals() {
      const grouped = {}

      this.expressMeals.forEach(item => {
        item.selectTypes.forEach(selectType => {
          const type = this.typesSelect.find(tag => tag.id === selectType.id)
          const group = {
            selectTypeName: type ? type.value : null,
            selectTypeId: type ? type.id : null,
            meals: [],
          }

          if (type) {
            if (!grouped[type.position]) {
              this.removeTypeFromList(type.id)
              grouped[type.position] = group
            }

            grouped[type.position].meals.push(item)
          } else {
            if (!grouped.null) {
              grouped.null = group
            }

            grouped.null.meals.push(item)
          }
        })
      })

      for (const position in grouped) {
        grouped[position].meals.push({})
      }

      this.groupedExpressMeals = grouped
    },
    addType() {
      if (this.chosenType) {
        const typeGroup = {}
        const type = this.typesSelect.find(tag => tag.id === this.chosenType)
        typeGroup[type.position] = {
          selectTypeName: type.value,
          selectTypeId: type.id,
          meals: [{}],
        }
        Object.assign(this.groupedExpressMeals, typeGroup)
        this.$forceUpdate()
        this.removeTypeFromList(this.chosenType)
      }
    },
    removeType(typeId) {
      for (const position in this.groupedExpressMeals) {
        if (this.groupedExpressMeals[position].selectTypeId === typeId) {
          delete this.groupedExpressMeals[position]
          break
        }
      }

      this.addTypeToList(typeId)
      this.$forceUpdate()
    },
    removeTypeFromList(typeId) {
      this.availableTypes = this.availableTypes.filter(item => item.id !== typeId)
    },
    addTypeToList(typeId) {
      const type = this.typesSelect.find(item => item.id === typeId)
      if (type) {
        this.availableTypes.push(type)
      }
    },
    back() {
      this.$router.push({
        name: 'menu-planner-select-show',
        params: {
          date: this.date,
          brand: this.brand,
        }
      })
    },
    showModal() {
      $('#modal').modal()
    },
    copyMenu() {
      $('#modal').modal('hide')
      this.expressMeals = []

      this.$emit('loading-start')
      this.$http.post(Routing.generate('api_post_copy_menu_express_planner'), {
        date: this.date,
        copyFromDate: this.copyFromDate,
        toBrandId: this.brandInfo.id,
        fromBrandId: this.brandInfo.id === this.fromBrand ? null : this.fromBrand,
      }).then(async() => {
        await this.getTypesSelect()
        this.getPlannedMeals()
        this.$emit('loading-stop')
      }, response => {
        this.handleAjaxError(response)
      })
    },
  },
}
</script>

<style scoped>
.planner-multiselect > .multiselect {
  min-height: unset;
}

.planner-multiselect > .multiselect > .multiselect__tags {
  min-height: unset;
  padding: 6px 40px 0 8px;
  border: 1px solid #ccc;
}

.pull-right {
  padding-right: 15px;
}
</style>
